import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, Tag, Plus, X, TrendingUp, TrendingDown, ArrowUp, ArrowDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { TruncatedText } from "@/components/ui/truncated-text";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";
import { isKeywordMetricPending, KEYWORD_PENDING_LABEL } from "@/utils/keywordPendingDisplay";
import type { KeywordTheme } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";

export function KeywordMetricValue({ value }: { value: number | null | undefined }) {
  if (isKeywordMetricPending(value)) {
    return <span className="text-muted-foreground">{KEYWORD_PENDING_LABEL}</span>;
  }
  if (value == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  // AUT-199: KPS/relevance are whole-number metrics; suggestion payloads (and the
  // optimistic theme written on accept) can carry decimals, so round for a
  // consistent display across every theme keyword table.
  return <>{Math.round(value)}</>;
}

/** Toggles an item in a Set immutably — for use with React state setters. */
export function TrendCell({ trend }: { trend: string | null | undefined }) {
  if (trend == null) return <span className="text-muted-foreground text-xs">—</span>;
  const n = Number(trend);
  const positive = n >= 0;
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 text-xs tabular-nums font-medium",
      positive ? "text-[hsl(var(--trend-positive))]" : "text-destructive",
    )}>
      {positive ? <TrendingUp className="h-3 w-3 shrink-0" /> : <TrendingDown className="h-3 w-3 shrink-0" />}
      {Math.abs(n)}%
    </span>
  );
}

export function toggleSetItem<T>(prev: Set<T>, item: T): Set<T> {
  const next = new Set(prev);
  next.has(item) ? next.delete(item) : next.add(item);
  return next;
}

/** Aggregate helpers for keyword metric columns (rank, kps, relevance). -1 means "no data". */
export function avg(values: (number | null | undefined)[]): number | null {
  const valid = values
    .filter((v): v is NonNullable<typeof v> => v != null)
    .map(Number)
    .filter((v) => !isNaN(v) && v > -1);
  return valid.length
    ? Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    : null;
}

export function sum(values: (number | null | undefined)[]): number | null {
  const valid = values
    .filter((v): v is NonNullable<typeof v> => v != null)
    .map(Number)
    .filter((v) => !isNaN(v));
  return valid.length ? valid.reduce((a, b) => a + b, 0) : null;
}

/** Aggregate up/down trend indicator shown in a group row's Trend column, shared by per-theme and Unthemed groups. */
export function GroupTrendSummary({ keywords }: { keywords: Keyword[] }) {
  const trendUp = keywords.filter((k) => Number(k.search_volume_trend ?? 0) > 0).length;
  const trendDown = keywords.filter((k) => Number(k.search_volume_trend ?? 0) < 0).length;
  return (
    <div className="flex items-center justify-center gap-2 text-xs font-medium">
      {keywords.length > 0 ? (
        <>
          <span className="inline-flex items-center gap-0.5 text-[hsl(var(--trend-positive))] tabular-nums">
            <ArrowUp className="h-3 w-3" strokeWidth={2.5} />
            {trendUp}
          </span>
          <span className="inline-flex items-center gap-0.5 text-destructive tabular-nums">
            <ArrowDown className="h-3 w-3" strokeWidth={2.5} />
            {trendDown}
          </span>
        </>
      ) : <span className="text-muted-foreground">—</span>}
    </div>
  );
}

/** Tri-state "select all" derivation for a group's checkbox, shared by every select-all-within-a-group control (per-theme, Unthemed). */
export function getGroupSelectionState(
  ids: number[],
  selected: Set<number>,
): { allSelected: boolean; someSelected: boolean } {
  if (ids.length === 0) return { allSelected: false, someSelected: false };
  const selectedCount = ids.reduce((n, id) => n + (selected.has(id) ? 1 : 0), 0);
  return {
    allSelected: selectedCount === ids.length,
    someSelected: selectedCount > 0 && selectedCount < ids.length,
  };
}

/** Removable chip for a selected keyword in the modal footer. */
export function SelectedKeywordChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 bg-muted text-sm px-2 py-0.5 rounded-full">
      {label}
      <button
        onClick={onRemove}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/** Colored rank pill used in ThemePanel and AddThemeModal keyword tables. */
export function RankBadge({ rank, emptyLabel = "—" }: { rank: number | null; emptyLabel?: string }) {
  if (rank == null)
    return <span className="text-muted-foreground text-xs">{emptyLabel}</span>;
  const color =
    rank <= 10
      ? "bg-green-100 text-green-800"
      : rank <= 20
        ? "bg-emerald-50 text-emerald-700"
        : rank <= 35
          ? "bg-yellow-100 text-yellow-800"
          : rank <= 50
            ? "bg-orange-100 text-orange-800"
            : "bg-red-100 text-red-800";
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-semibold min-w-[28px]",
        color,
      )}
    >
      {rank}
    </span>
  );
}

export type KeywordSuggestionRow = {
  key: string | number;
  label: string;
  subLabel?: string | null;
  rank: number | null;
  kps: number | null;
  searchVolume: number | null;
  relevance: number | null;
  trend: string | null;
  selected: boolean;
  onSelect: () => void;
};

/** Boxed, bordered keyword suggestion/results table shared by AddKeywordToThemeModal's tabs. */
export function KeywordSuggestionTable({ rows }: { rows: KeywordSuggestionRow[] }) {
  return (
    <div className="mx-6 mt-3 rounded-sm border border-border max-h-[320px] min-h-0 overflow-y-auto">
      {/* Bypass the shared Table wrapper's own inner overflow-auto div — sticky
          positioning must bind to *this* box, the actual scrolling ancestor. */}
      <table className="w-full caption-bottom text-sm border-collapse">
        <TableHeader>
          <TableRow>
            <TableHead className="h-9 px-3">Keyword</TableHead>
            <TableHead className="h-9 w-[70px] text-center px-2">Rank</TableHead>
            <TableHead className="h-9 w-[60px] text-center px-2">KPS</TableHead>
            <TableHead className="h-9 w-[100px] text-center px-2">Search Vol.</TableHead>
            <TableHead className="h-9 w-[55px] text-center px-2">Rel.</TableHead>
            <TableHead className="h-9 w-[70px] text-center px-2">Trend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              onClick={row.onSelect}
              className={cn("cursor-pointer", row.selected && "bg-primary/10")}
            >
              <TableCell className="px-3 py-2">
                <div className="grid grid-cols-[16px_minmax(0,1fr)] items-center gap-2">
                  {row.selected ? (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  ) : (
                    <span className="inline-block h-4 w-4 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <TruncatedText className="text-sm">{row.label}</TruncatedText>
                    {row.subLabel && (
                      <TruncatedText className="text-[10px] text-muted-foreground">{row.subLabel}</TruncatedText>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-center px-2 py-2">
                <RankBadge rank={row.rank} />
              </TableCell>
              <TableCell className="text-center px-2 py-2 text-xs">
                <KeywordMetricValue value={row.kps} />
              </TableCell>
              <TableCell className="text-center px-2 py-2 text-xs">
                {row.searchVolume != null ? formatMetricNumber(row.searchVolume) : <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="text-center px-2 py-2 text-xs">
                <KeywordMetricValue value={row.relevance} />
              </TableCell>
              <TableCell className="text-center px-2 py-2">
                <TrendCell trend={row.trend} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </table>
    </div>
  );
}

/** "Head Term" badge shown on the keyword that is the head term of a theme. */
export function HeadTermBadge() {
  return (
    <Badge className="text-[10px] px-1.5 py-0 font-medium bg-primary/15 text-foreground hover:bg-primary/15 shrink-0">
      Head Term
    </Badge>
  );
}

/**
 * v3-style theme picker submenu for keyword row 3-dot menus.
 * Shows all themes with a checkmark on the current one and an inline "Remove" link.
 */
export function ThemePickerSubmenu({
  themes,
  currentThemeId,
  onSelect,
  onRemove,
}: {
  themes: { id: number; name: string }[];
  currentThemeId?: number;
  onSelect: (themeId: number) => void;
  onRemove?: () => void;
}) {
  if (themes.length === 0) return null;
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Tag className="h-4 w-4 mr-2" />
        {currentThemeId ? "Change theme…" : "Set theme…"}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[280px] overflow-y-auto">
        {themes.map((t) => {
          const isCurrent = t.id === currentThemeId;
          return (
            <DropdownMenuItem
              key={t.id}
              onClick={() => {
                if (!isCurrent) onSelect(t.id);
              }}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center min-w-0">
                <Check
                  className={cn(
                    "h-4 w-4 mr-2 shrink-0",
                    isCurrent ? "opacity-100" : "opacity-0",
                  )}
                />
                {/* eslint-disable-next-line signal/no-unwrapped-clipped-text -- inside-an-open-overlay */}
                <span className="truncate">{t.name}</span>
              </span>
              {isCurrent && onRemove && (
                <span
                  role="button"
                  className="text-xs text-destructive hover:underline shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove();
                  }}
                >
                  Remove
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/**
 * Theme column cell: shows the theme name, an "Add" dropdown for unthemed keywords,
 * or "—" in read-only mode.
 */
export function ThemeColumnCell({
  themeName,
  themes,
  keywordId,
  onSelect,
  onOpenAddTheme,
  readOnly,
}: {
  themeName?: string;
  themes: { id: number; name: string }[];
  keywordId?: number;
  onSelect: (themeId: number) => void;
  onOpenAddTheme?: (keywordId?: number) => void;
  readOnly?: boolean;
}) {
  if (themeName) {
    return (
      <div className="grid grid-cols-[minmax(0,1fr)]">
        <TruncatedText className="text-xs text-muted-foreground">
          {themeName}
        </TruncatedText>
      </div>
    );
  }
  if (readOnly || (themes.length === 0 && !onOpenAddTheme)) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="text-primary hover:underline text-xs">
          Add
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[280px] overflow-y-auto"
      >
        {themes.map((t) => (
          <DropdownMenuItem key={t.id} onClick={() => onSelect(t.id)}>
            {t.name}
          </DropdownMenuItem>
        ))}
        {onOpenAddTheme && (
          <DropdownMenuItem onClick={() => onOpenAddTheme(keywordId)}>
            <Plus className="h-4 w-4 mr-2" />
            New theme…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Confirmation dialog shown before permanently deleting a theme. */
export function DeleteThemeDialog({
  theme,
  onConfirm,
  onCancel,
}: {
  theme: KeywordTheme | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDeleteDialog
      open={!!theme}
      onOpenChange={(open) => { if (!open) onCancel(); }}
      title={`Delete the "${theme?.name}" theme?`}
      description="Keywords in this theme will become unthemed. This action cannot be undone."
      confirmLabel="Yes, delete"
      onConfirm={onConfirm}
    />
  );
}
